import type { QueryClient } from '@tanstack/react-query';
import {
  persistQueryClientRestore,
  persistQueryClientSubscribe,
  type PersistedClient,
  type Persister,
} from '@tanstack/react-query-persist-client';
import type { PersonalStore } from '@/offline/personalStore';

/**
 * Keeping the figures a person last saw, so the app has something to show when
 * there is no connection.
 *
 * They used to live in plain `localStorage` for a day (.docs/012). They now
 * live in the person's own copy on this PC, sealed with their key, for a week
 * (.docs/023):
 *
 * 1. **They belong to one person.** Nobody else's password or key opens them,
 *    so they need not be thrown away when somebody else signs in.
 * 2. **They come back when the copy opens**, at sign-in, and are written back,
 *    throttled, as the screens load more.
 * 3. **They expire**: a week matches how long somebody may sign in offline.
 *    A new build throws them away, rather than restoring a shape it does not
 *    expect.
 */

/** As long as somebody may sign in with no connection. */
export const MAX_AGE_MS = 7 * 24 * 60 * 60_000;

/** Written at most this often, however busy the screen is. */
const THROTTLE_MS = 2_000;

const NAME = 'figures';

/** Stores the query cache in a person's copy, sealed. */
export function copyPersister(store: PersonalStore): Persister {
  let pending: PersistedClient | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  return {
    persistClient: (client) => {
      pending = client;
      timer ??= setTimeout(() => {
        timer = null;
        const next = pending;
        pending = null;
        // A copy closed at sign-out meanwhile refuses the write. Nothing is
        // lost that matters: these are figures, read again next time.
        if (next) void store.write(NAME, next).catch(() => undefined);
      }, THROTTLE_MS);
    },
    restoreClient: async () => (await store.read<PersistedClient>(NAME)) ?? undefined,
    removeClient: () => store.remove(NAME),
  };
}

let stopKeeping: (() => void) | null = null;

/**
 * Brings back what this person last saw and keeps the cache written to their
 * copy from now on. With no copy — signed out — it stops, and the figures in
 * memory are emptied, so the next person at this window starts with nothing.
 */
export async function keepFiguresIn(store: PersonalStore | null, client: QueryClient): Promise<void> {
  stopKeeping?.();
  stopKeeping = null;

  if (!store) {
    client.clear();
    return;
  }

  const persister = copyPersister(store);
  try {
    await persistQueryClientRestore({ queryClient: client, persister, maxAge: MAX_AGE_MS, buster: __APP_VERSION__ });
  } catch {
    // An unreadable cache is the same as none: the screens load afresh.
  }
  stopKeeping = persistQueryClientSubscribe({ queryClient: client, persister, buster: __APP_VERSION__ });
}

/**
 * When the newest thing in the cache was fetched, so a screen can say how old
 * what it is showing actually is. Null when nothing has been loaded yet.
 */
export function figuresUpdatedAt(client: QueryClient): Date | null {
  let newest = 0;
  for (const query of client.getQueryCache().getAll()) {
    const at = query.state.dataUpdatedAt;
    if (at > newest) newest = at;
  }
  return newest > 0 ? new Date(newest) : null;
}

// What an older build kept in plain `localStorage`. Pay and stock values were
// in there, readable by anybody at the PC; they are removed for good.
try {
  localStorage.removeItem('poultry.figures');
  localStorage.removeItem('poultry.figures.owner');
} catch {
  // Private mode: nothing was stored.
}
